import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { useAuth } from './AuthContext';
import {
  TutorialDefinition,
  UserTutorialProgress,
  TutorialStats,
  getTutorialByKey,
  getAllTutorialKeys
} from '../config/tutorials';

interface TutorialContextType {
  // Active tutorial state
  activeTutorial: TutorialDefinition | null;
  currentStep: number;
  isActive: boolean;

  // Tutorial management
  startTutorial: (tutorialKey: string) => Promise<void>;
  nextStep: () => Promise<void>;
  previousStep: () => void;
  goToStep: (stepIndex: number) => void;
  skipTutorial: () => Promise<void>;
  completeTutorial: () => Promise<void>;
  resetTutorial: (tutorialKey: string) => Promise<void>;

  // Auto-trigger logic
  checkAndStartTutorial: (tutorialKey: string) => Promise<void>;

  // Progress tracking
  getAllProgress: () => Promise<UserTutorialProgress[]>;
  getProgress: (tutorialKey: string) => Promise<UserTutorialProgress | null>;
  getTutorialStats: () => Promise<TutorialStats | null>;

  // Loading state
  isLoading: boolean;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export const useTutorial = () => {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return context;
};

interface TutorialProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.herosofthegrid.simwebtech.com/api';

export const TutorialProvider: React.FC<TutorialProviderProps> = ({ children }) => {
  const { token } = useAuth();
  const [activeTutorial, setActiveTutorial] = useState<TutorialDefinition | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const isActive = activeTutorial !== null;

  // Helper to make authorized API calls
  const apiCall = async (endpoint: string, options: RequestInit = {}) => {
    if (!token) {
      throw new Error('Not authenticated');
    }

    const url = `${API_BASE_URL}/tutorial/${endpoint}`;
    console.log('[TutorialContext] API Call:', options.method || 'GET', url);

    const response = await fetch(url, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    console.log('[TutorialContext] API Response:', response.status, response.statusText);

    if (!response.ok) {
      const error = await response.text();
      console.error('[TutorialContext] API Error:', error);
      throw new Error(error || 'API request failed');
    }

    const data = await response.json();
    console.log('[TutorialContext] API Data:', data);
    return data;
  };

  /**
   * Start a tutorial
   */
  const startTutorial = useCallback(async (tutorialKey: string) => {
    try {
      console.log('[TutorialContext] Starting tutorial:', tutorialKey);
      setIsLoading(true);
      const tutorial = getTutorialByKey(tutorialKey);

      if (!tutorial) {
        throw new Error(`Tutorial not found: ${tutorialKey}`);
      }

      console.log('[TutorialContext] Tutorial found:', tutorial.title, 'Steps:', tutorial.steps.length);

      // Call backend to mark tutorial as started
      const result = await apiCall('start', {
        method: 'POST',
        body: JSON.stringify({
          tutorialKey,
          totalSteps: tutorial.steps.length
        })
      });

      console.log('[TutorialContext] Tutorial started, backend response:', result);

      setActiveTutorial(tutorial);
      setCurrentStep(0);
    } catch (error) {
      console.error('[TutorialContext] Failed to start tutorial:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  /**
   * Advance to next step
   */
  const nextStep = useCallback(async () => {
    if (!activeTutorial) return;

    const nextStepIndex = currentStep + 1;
    console.log('[TutorialContext] Next step:', nextStepIndex, '/', activeTutorial.steps.length);

    if (nextStepIndex >= activeTutorial.steps.length) {
      // Tutorial complete
      console.log('[TutorialContext] Tutorial finished, calling completeTutorial');
      await completeTutorial();
      return;
    }

    try {
      // Update backend progress
      console.log('[TutorialContext] Updating step progress to:', nextStepIndex);
      const result = await apiCall('step', {
        method: 'POST',
        body: JSON.stringify({
          tutorialKey: activeTutorial.key,
          stepIndex: nextStepIndex
        })
      });
      console.log('[TutorialContext] Step updated, backend response:', result);

      setCurrentStep(nextStepIndex);
    } catch (error) {
      console.error('[TutorialContext] Failed to update step:', error);
      // Still advance locally even if backend fails
      setCurrentStep(nextStepIndex);
    }
  }, [activeTutorial, currentStep, token]);

  /**
   * Go back to previous step
   */
  const previousStep = useCallback(() => {
    if (!activeTutorial || currentStep === 0) return;
    setCurrentStep(currentStep - 1);
  }, [activeTutorial, currentStep]);

  /**
   * Jump to a specific step
   */
  const goToStep = useCallback((stepIndex: number) => {
    if (!activeTutorial) return;
    if (stepIndex < 0 || stepIndex >= activeTutorial.steps.length) return;
    setCurrentStep(stepIndex);
  }, [activeTutorial]);

  /**
   * Skip the current tutorial
   */
  const skipTutorial = useCallback(async () => {
    if (!activeTutorial) return;

    try {
      console.log('[TutorialContext] Skipping tutorial:', activeTutorial.key);
      const result = await apiCall(`skip/${activeTutorial.key}`, { method: 'POST' });
      console.log('[TutorialContext] Tutorial skipped, backend response:', result);
    } catch (error) {
      console.error('[TutorialContext] Failed to skip tutorial:', error);
    } finally {
      setActiveTutorial(null);
      setCurrentStep(0);
    }
  }, [activeTutorial, token]);

  /**
   * Complete the current tutorial
   */
  const completeTutorial = useCallback(async () => {
    if (!activeTutorial) return;

    try {
      console.log('[TutorialContext] Completing tutorial:', activeTutorial.key);
      const result = await apiCall(`complete/${activeTutorial.key}`, { method: 'POST' });
      console.log('[TutorialContext] Tutorial completed, backend response:', result);
    } catch (error) {
      console.error('[TutorialContext] Failed to complete tutorial:', error);
    } finally {
      setActiveTutorial(null);
      setCurrentStep(0);
    }
  }, [activeTutorial, token]);

  /**
   * Reset a tutorial (for replay)
   */
  const resetTutorial = useCallback(async (tutorialKey: string) => {
    try {
      await apiCall(`reset/${tutorialKey}`, { method: 'POST' });
    } catch (error) {
      console.error('Failed to reset tutorial:', error);
      throw error;
    }
  }, [token]);

  /**
   * Check if tutorial should be shown and optionally start it
   */
  const checkAndStartTutorial = useCallback(async (tutorialKey: string) => {
    if (!token) return;

    try {
      const data = await apiCall(`should-show/${tutorialKey}`);

      if (data.shouldShow) {
        // For now, auto-start the tutorial
        // In the future, we could show a prompt first
        await startTutorial(tutorialKey);
      }
    } catch (error) {
      console.error('Failed to check tutorial status:', error);
    }
  }, [token, startTutorial]);

  /**
   * Get all progress for current user
   */
  const getAllProgress = useCallback(async (): Promise<UserTutorialProgress[]> => {
    if (!token) return [];

    try {
      return await apiCall('progress');
    } catch (error) {
      console.error('Failed to get tutorial progress:', error);
      return [];
    }
  }, [token]);

  /**
   * Get progress for a specific tutorial
   */
  const getProgress = useCallback(async (tutorialKey: string): Promise<UserTutorialProgress | null> => {
    if (!token) return null;

    try {
      return await apiCall(`progress/${tutorialKey}`);
    } catch (error) {
      console.error('Failed to get tutorial progress:', error);
      return null;
    }
  }, [token]);

  /**
   * Get tutorial statistics
   */
  const getTutorialStats = useCallback(async (): Promise<TutorialStats | null> => {
    if (!token) return null;

    try {
      const allKeys = getAllTutorialKeys();
      return await apiCall(`stats?tutorialKeys=${allKeys.join(',')}`);
    } catch (error) {
      console.error('Failed to get tutorial stats:', error);
      return null;
    }
  }, [token]);

  const value: TutorialContextType = {
    activeTutorial,
    currentStep,
    isActive,
    startTutorial,
    nextStep,
    previousStep,
    goToStep,
    skipTutorial,
    completeTutorial,
    resetTutorial,
    checkAndStartTutorial,
    getAllProgress,
    getProgress,
    getTutorialStats,
    isLoading
  };

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
